import { ScatterChart, Scatter, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine, Cell } from 'recharts';
import { useGA4PageEngagement } from '@/hooks/useGA4EngagementData';
import { Loader2, TrendingUp, AlertTriangle, Sparkles, Star, Target } from 'lucide-react';
import { useState, useMemo } from 'react';

interface PageData {
  pagePath: string;
  pageTitle: string;
  views: number;
  avgEngagementTime: number;
  bounceRate: number;
  users: number;
  quadrant: 'stars' | 'hidden-gems' | 'fix-ux' | 'low-priority';
}

// Quadrant colors
const QUADRANT_COLORS = {
  'stars': '#22c55e',        // Green - High traffic, high engagement
  'hidden-gems': '#3b82f6',  // Blue - Low traffic, high engagement
  'fix-ux': '#f59e0b',       // Amber - High traffic, low engagement
  'low-priority': '#6b7280', // Gray - Low traffic, low engagement
};

const QUADRANT_INFO = {
  'stars': { label: 'Stars', icon: Star, description: 'High traffic & engagement - Keep optimizing!' },
  'hidden-gems': { label: 'Hidden Gems', icon: Sparkles, description: 'High engagement, needs more traffic - Promote these!' },
  'fix-ux': { label: 'Fix UX', icon: AlertTriangle, description: 'High traffic but low engagement - Improve content!' },
  'low-priority': { label: 'Low Priority', icon: Target, description: 'Low traffic & engagement - Consider retiring' },
};

export function EngagementMatrixView() {
  const { data, isLoading, error } = useGA4PageEngagement(100);
  const [selectedQuadrant, setSelectedQuadrant] = useState<string | null>(null);
  const [hoveredPoint, setHoveredPoint] = useState<PageData | null>(null);

  // Process data and assign quadrants
  const { processedData, medianViews, medianEngagement, quadrantCounts } = useMemo(() => {
    if (!data || data.length === 0) {
      return { processedData: [], medianViews: 0, medianEngagement: 0, quadrantCounts: {} };
    }

    // Calculate medians for threshold lines
    const sortedViews = [...data].sort((a, b) => a.views - b.views);
    const sortedEngagement = [...data].sort((a, b) => a.avgEngagementTime - b.avgEngagementTime);

    const medViews = sortedViews[Math.floor(sortedViews.length / 2)]?.views || 0;
    const medEngagement = sortedEngagement[Math.floor(sortedEngagement.length / 2)]?.avgEngagementTime || 0;

    // Assign quadrants
    const processed: PageData[] = data.map((page) => {
      const isHighTraffic = page.views >= medViews;
      const isHighEngagement = page.avgEngagementTime >= medEngagement;

      let quadrant: PageData['quadrant'];
      if (isHighTraffic && isHighEngagement) quadrant = 'stars';
      else if (!isHighTraffic && isHighEngagement) quadrant = 'hidden-gems';
      else if (isHighTraffic && !isHighEngagement) quadrant = 'fix-ux';
      else quadrant = 'low-priority';

      return { ...page, quadrant };
    });

    // Count by quadrant
    const counts = processed.reduce((acc, p) => {
      acc[p.quadrant] = (acc[p.quadrant] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    return { processedData: processed, medianViews: medViews, medianEngagement: medEngagement, quadrantCounts: counts };
  }, [data]);

  // Filter by selected quadrant
  const filteredData = selectedQuadrant
    ? processedData.filter(p => p.quadrant === selectedQuadrant)
    : processedData;

  // Custom tooltip
  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const page = payload[0].payload as PageData;
      return (
        <div className="glass-panel p-3 rounded-lg shadow-lg max-w-xs">
          <p className="font-medium text-sm truncate">{page.pageTitle || page.pagePath}</p>
          <p className="text-xs text-muted-foreground truncate mb-2">{page.pagePath}</p>
          <div className="space-y-1 text-xs">
            <div className="flex justify-between gap-4">
              <span className="text-muted-foreground">Page Views:</span>
              <span className="font-medium">{page.views.toLocaleString()}</span>
            </div>
            <div className="flex justify-between gap-4">
              <span className="text-muted-foreground">Avg. Time:</span>
              <span className="font-medium">{formatTime(page.avgEngagementTime)}</span>
            </div>
            <div className="flex justify-between gap-4">
              <span className="text-muted-foreground">Users:</span>
              <span className="font-medium">{page.users.toLocaleString()}</span>
            </div>
            <div className="flex justify-between gap-4">
              <span className="text-muted-foreground">Bounce Rate:</span>
              <span className="font-medium">{page.bounceRate.toFixed(1)}%</span>
            </div>
          </div>
          <div
            className="mt-2 pt-2 border-t border-border text-xs font-medium"
            style={{ color: QUADRANT_COLORS[page.quadrant] }}
          >
            {QUADRANT_INFO[page.quadrant].label}
          </div>
        </div>
      );
    }
    return null;
  };

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto mb-3" />
          <p className="text-sm text-muted-foreground">Loading engagement data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-center">
          <AlertTriangle className="h-8 w-8 text-destructive mx-auto mb-3" />
          <p className="text-sm text-destructive">Failed to load engagement data</p>
        </div>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-center max-w-md">
          <Target className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <h3 className="font-semibold mb-2">No Engagement Data</h3>
          <p className="text-sm text-muted-foreground">
            Connect a GA4 property with page view data to see the engagement matrix.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col p-4 gap-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Target className="h-5 w-5 text-primary" />
            Engagement Matrix
          </h2>
          <p className="text-sm text-muted-foreground">
            Traffic volume vs. engagement quality (Last 28 days)
          </p>
        </div>
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <TrendingUp className="h-4 w-4" />
          <span>{processedData.length} pages analyzed</span>
        </div>
      </div>

      {/* Quadrant Legend/Filter */}
      <div className="grid grid-cols-4 gap-2">
        {(Object.keys(QUADRANT_INFO) as Array<keyof typeof QUADRANT_INFO>).map((key) => {
          const info = QUADRANT_INFO[key];
          const Icon = info.icon;
          const count = quadrantCounts[key] || 0;
          const isSelected = selectedQuadrant === key;

          return (
            <button
              key={key}
              onClick={() => setSelectedQuadrant(isSelected ? null : key)}
              className={`glass-panel p-3 rounded-lg text-left transition-all ${
                isSelected ? 'ring-2 ring-primary' : 'hover:bg-muted/50'
              }`}
            >
              <div className="flex items-center gap-2 mb-1">
                <Icon className="h-4 w-4" style={{ color: QUADRANT_COLORS[key] }} />
                <span className="font-medium text-sm" style={{ color: QUADRANT_COLORS[key] }}>
                  {info.label}
                </span>
              </div>
              <div className="text-2xl font-bold">{count}</div>
              <p className="text-xs text-muted-foreground line-clamp-2">{info.description}</p>
            </button>
          );
        })}
      </div>

      {/* Scatter Chart */}
      <div className="flex-1 glass-panel rounded-lg p-4 min-h-[300px]">
        <ResponsiveContainer width="100%" height="100%">
          <ScatterChart margin={{ top: 20, right: 30, bottom: 20, left: 20 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.5} />
            <XAxis
              type="number"
              dataKey="views"
              name="Page Views"
              stroke="hsl(var(--muted-foreground))"
              fontSize={12}
              tickFormatter={(v) => v >= 1000 ? `${(v/1000).toFixed(0)}k` : v}
              label={{
                value: 'Page Views (Traffic Volume)',
                position: 'bottom',
                offset: 0,
                fontSize: 11,
                fill: 'hsl(var(--muted-foreground))'
              }}
            />
            <YAxis
              type="number"
              dataKey="avgEngagementTime"
              name="Avg Engagement Time"
              stroke="hsl(var(--muted-foreground))"
              fontSize={12}
              tickFormatter={(v) => formatTime(v)}
              label={{
                value: 'Avg. Time on Page',
                angle: -90,
                position: 'insideLeft',
                fontSize: 11,
                fill: 'hsl(var(--muted-foreground))'
              }}
            />

            {/* Threshold lines */}
            <ReferenceLine
              x={medianViews}
              stroke="hsl(var(--muted-foreground))"
              strokeDasharray="5 5"
              strokeOpacity={0.7}
            />
            <ReferenceLine
              y={medianEngagement}
              stroke="hsl(var(--muted-foreground))"
              strokeDasharray="5 5"
              strokeOpacity={0.7}
            />

            <Tooltip content={<CustomTooltip />} />

            <Scatter
              data={filteredData}
              onMouseEnter={(data) => setHoveredPoint(data as PageData)}
              onMouseLeave={() => setHoveredPoint(null)}
            >
              {filteredData.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={QUADRANT_COLORS[entry.quadrant]}
                  opacity={hoveredPoint && hoveredPoint.pagePath !== entry.pagePath ? 0.3 : 0.8}
                  r={Math.max(4, Math.min(12, entry.users / 100))}
                />
              ))}
            </Scatter>
          </ScatterChart>
        </ResponsiveContainer>
      </div>

      {/* Top pages by quadrant */}
      {selectedQuadrant && (
        <div className="glass-panel rounded-lg p-4 max-h-48 overflow-auto">
          <h3 className="font-medium mb-3 flex items-center gap-2">
            {(() => {
              const Icon = QUADRANT_INFO[selectedQuadrant as keyof typeof QUADRANT_INFO].icon;
              return <Icon className="h-4 w-4" style={{ color: QUADRANT_COLORS[selectedQuadrant as keyof typeof QUADRANT_COLORS] }} />;
            })()}
            {QUADRANT_INFO[selectedQuadrant as keyof typeof QUADRANT_INFO].label} Pages
          </h3>
          <div className="space-y-2">
            {filteredData.slice(0, 10).map((page, i) => (
              <div key={i} className="flex items-center justify-between text-sm">
                <div className="flex-1 truncate pr-4">
                  <span className="text-muted-foreground mr-2">{i + 1}.</span>
                  {page.pageTitle || page.pagePath}
                </div>
                <div className="flex items-center gap-4 text-xs text-muted-foreground">
                  <span>{page.views.toLocaleString()} views</span>
                  <span>{formatTime(page.avgEngagementTime)}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function formatTime(seconds: number): string {
  if (seconds < 60) return `${Math.round(seconds)}s`;
  const mins = Math.floor(seconds / 60);
  const secs = Math.round(seconds % 60);
  return `${mins}m ${secs}s`;
}
